// The icons a Pictorial Bar draws its bars with (Studio.PictorialOptions),
// drawn in-house. Each is one filled SVG path whose bounds are exactly its
// width and height: ECharts stretches a path:// symbol to its size, so the
// bars size each icon to its own shape. Holes (a tyre's, a clock's) wind
// against the shape around them, as the default nonzero fill needs.
export interface PictorialIcon {
  id: string;
  label: string;
  path: string;
  width: number;
  height: number;
}

// A circle, drawn anticlockwise unless asked (the rectangles below all wind
// clockwise)
const circle = ({
  cx,
  cy,
  r,
  isClockwise = false,
}: {
  cx: number;
  cy: number;
  r: number;
  isClockwise?: boolean;
}) => {
  const sweep = isClockwise ? 1 : 0;
  return `M${cx - r} ${cy}a${r} ${r} 0 1 ${sweep} ${r * 2} 0a${r} ${r} 0 1 ${sweep} ${-r * 2} 0z`;
};

const wheels = (xs: number[], cy: number) =>
  xs.map((cx) => circle({ cx, cy, r: 2 })).join('');

export const PICTORIAL_ICONS: PictorialIcon[] = [
  {
    id: 'truck',
    label: 'Truck',
    path: `M0 0h20v10H0zM21 3h7l4 4v3H21z${wheels([4, 9, 24, 29], 12)}`,
    width: 32,
    height: 14,
  },
  {
    id: 'tractor',
    label: 'Tractor',
    path: `M4 0h9l5 5v6H4zM0 7h4v4H0z${wheels([4, 14], 12)}`,
    width: 18,
    height: 14,
  },
  {
    id: 'dryVan',
    label: 'Trailer',
    path: `M0 0h24v10H0zM3 10h1.5v3H3z${wheels([18, 22], 12)}`,
    width: 24,
    height: 14,
  },
  {
    id: 'reefer',
    label: 'Reefer',
    path: `M0 2h2v6H0zM2 0h24v10H2zM5 10h1.5v3H5z${wheels([20, 24], 12)}`,
    width: 26,
    height: 14,
  },
  {
    id: 'flatbed',
    label: 'Flatbed',
    path: `M3 2h8v5H3zM12 0h8v7h-8zM0 7h24v2H0zM4 9h1.5v3H4z${wheels([17, 22], 11)}`,
    width: 24,
    height: 13,
  },
  {
    id: 'tanker',
    label: 'Tanker',
    path: `M4 0h16a4 4 0 0 1 0 8H4a4 4 0 0 1 0-8zM2 8h20v2H2zM5 10h1.5v3H5z${wheels([17, 22], 12)}`,
    width: 24,
    height: 14,
  },
  {
    id: 'fuel',
    label: 'Fuel',
    path: 'M1 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v17H1zM0 19h14v2H0zM13 5h3l2 3v8h-2V9l-1-2h-2z',
    width: 18,
    height: 21,
  },
  {
    id: 'driver',
    label: 'Driver',
    path: `${circle({ cx: 6, cy: 3.5, r: 3.5 })}M0 18v-5a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v5z`,
    width: 12,
    height: 18,
  },
  {
    id: 'box',
    label: 'Box',
    path: 'M0 3l8-3 8 3v10l-8 3-8-3z',
    width: 16,
    height: 16,
  },
  {
    id: 'pallet',
    label: 'Pallet',
    path: 'M4.5 0h6v6h-6zM1 6h6v6H1zM8 6h6v6H8zM0 12h15v1.5H0zM0.5 13.5h2V15h-2zM6.5 13.5h2V15h-2zM12.5 13.5h2V15h-2zM0 15h15v1H0z',
    width: 15,
    height: 16,
  },
  {
    id: 'tyre',
    label: 'Tyre',
    path: [
      circle({ cx: 8, cy: 8, r: 8 }),
      circle({ cx: 8, cy: 8, r: 4, isClockwise: true }),
      circle({ cx: 8, cy: 8, r: 1.5 }),
    ].join(''),
    width: 16,
    height: 16,
  },
  {
    id: 'wrench',
    label: 'Maintenance',
    path: 'M0 0h2v5h4V0h2v6a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1zM3 7h2v11a1 1 0 0 1-2 0z',
    width: 8,
    height: 19,
  },
  {
    id: 'location',
    label: 'Location',
    path: 'M6 0a6 6 0 0 1 6 6c0 4.5-6 10-6 10S0 10.5 0 6a6 6 0 0 1 6-6z',
    width: 12,
    height: 16,
  },
  {
    id: 'clock',
    label: 'Hours',
    path: [
      circle({ cx: 8, cy: 8, r: 8 }),
      circle({ cx: 8, cy: 8, r: 6.5, isClockwise: true }),
      'M7.25 3.5h1.5v5h-1.5zM8 7.25h4v1.5H8z',
    ].join(''),
    width: 16,
    height: 16,
  },
  {
    id: 'money',
    label: 'Money',
    path: [
      'M0 0h20v12H0z',
      circle({ cx: 10, cy: 6, r: 4 }),
      circle({ cx: 10, cy: 6, r: 2, isClockwise: true }),
    ].join(''),
    width: 20,
    height: 12,
  },
  {
    id: 'shield',
    label: 'Safety',
    path: 'M8 0l8 3v5c0 5-3.5 8.5-8 10C3.5 16.5 0 13 0 8V3z',
    width: 16,
    height: 18,
  },
  {
    id: 'warehouse',
    label: 'Warehouse',
    path: 'M0 6l11-6 11 6v12H0zM7 18h8v-8H7z',
    width: 22,
    height: 18,
  },
  {
    id: 'leaf',
    label: 'Emissions',
    path: 'M16 0C16 10 11 16 4 16c-2 0-3-.5-4-1C1 7 7 1 16 0z',
    width: 16,
    height: 16,
  },
];

export const DEFAULT_PICTORIAL_ICON = 'truck';

// The icon a Pictorial Bar draws with: its own, or a truck
export const getPictorialIcon = (id: string | undefined): PictorialIcon =>
  PICTORIAL_ICONS.find((icon) => icon.id === id) ??
  PICTORIAL_ICONS.find((icon) => icon.id === DEFAULT_PICTORIAL_ICON) ??
  PICTORIAL_ICONS[0];
